import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import AdminLogo from "@/components/AdminLogo";
import { MessageCircle, Clock, CheckCircle2 } from "lucide-react";

export default function EmployerPending() {
  const { t } = useTranslation();
  const location = useLocation();
  const email = location.state?.email || "";
  const detail = location.state?.message || "";
  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const waNumber = (settings?.whatsapp_number || "+673 8123456").replace(/[^\d+]/g, "");
  const waMsg = encodeURIComponent(
    `Halo Adelina, saya baru daftar sebagai employer${email ? ` (${email})` : ""}. Mohon verifikasi akun saya. Terima kasih.`
  );
  const waLink = `https://wa.me/${waNumber.replace(/^\+/, "")}?text=${waMsg}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 grain relative overflow-hidden py-16">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(circle, #b8860b 0%, transparent 70%)" }} />
      <div className="w-full max-w-lg p-9 bg-zinc-900/85 backdrop-blur-2xl rounded-[28px] border border-zinc-800/80 shadow-2xl relative">
        <div className="flex items-center gap-3 mb-8">
          <AdminLogo size={44} variant="light" />
          <div className="text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-medium">Employer Portal</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-amber-300 font-semibold mb-3">
          <Clock size={12} /> {t("emp.pending_overline", "Awaiting Verification")}
        </div>
        <h1 className="font-serif text-white text-3xl mb-4 tracking-tight">
          {t("emp.pending_title", "One last step")}
        </h1>
        <p className="text-zinc-400 leading-relaxed mb-6 text-[15px]">
          {detail || t("emp.pending_desc", "Your account is created but awaiting manual verification. Please chat our admin on WhatsApp — we typically verify within a few hours during business hours.")}
        </p>

        {email && (
          <div className="mb-6 p-3 rounded-xl bg-zinc-800/60 border border-zinc-700 text-sm text-zinc-300">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Your email</div>
            <div className="font-mono text-white">{email}</div>
          </div>
        )}

        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-6 py-3.5 rounded-full transition-all shadow-lg shadow-emerald-900/30"
          data-testid="emp-pending-wa"
        >
          <MessageCircle size={16} />
          {t("emp.pending_cta", "Chat admin on WhatsApp")}
        </a>

        <div className="mt-6 space-y-2 text-sm text-zinc-400">
          <div className="flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
            <span>{t("emp.pending_step1", "Send your business info & the candidate criteria you need.")}</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
            <span>{t("emp.pending_step2", "Admin will verify & activate your account.")}</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
            <span>{t("emp.pending_step3", "You will receive a confirmation and can log in immediately after.")}</span>
          </div>
        </div>

        <div className="mt-8 text-sm text-zinc-500 text-center">
          <Link to="/employer/login" className="hover:text-[#d4a017]" data-testid="emp-pending-goto-login">
            {t("emp.back_to_login", "← Back to sign in")}
          </Link>
        </div>
      </div>
    </div>
  );
}
